import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  FileDownload,
  Image as ImageIcon,
  Warning,
  CheckCircle,
  Info,
  Error,
} from '@mui/icons-material'
import { LineChart } from '@mui/x-charts/LineChart'
import { BarChart } from '@mui/x-charts/BarChart'
import { PieChart } from '@mui/x-charts/PieChart'
import * as htmlToImage from 'html-to-image'
import { GENERATE_DATA_VISUALIZATION } from './graphql'
import {
  Container,
  Header,
  QuerySection,
  ResultsSection,
  QueryInput,
  ActionButton,
  ChartContainer,
  TableWrapper,
  SqlSection,
} from './DataVisualization.styled'

type DataPoint = Record<string, any>

type ChartType = 'line' | 'bar' | 'pie' | 'table'

/**
 * Determines the best chart type based on the data structure
 */
const determineChartType = (data: DataPoint[]): ChartType => {
  if (!data || data.length === 0) return 'table'

  const firstRow = data[0]
  const keys = Object.keys(firstRow)

  // If we have exactly 2 columns, check if we can create a chart
  if (keys.length === 2) {
    const [key1, key2] = keys

    // Count numeric vs non-numeric values
    let numericKey: string | null = null
    let labelKey: string | null = null

    // Check which column is primarily numeric
    const key1IsNumeric = data.every((row) => {
      const val = row[key1]
      return (
        val === null ||
        val === undefined ||
        val === '' ||
        typeof val === 'number' ||
        !Number.isNaN(parseFloat(String(val)))
      )
    })

    const key2IsNumeric = data.every((row) => {
      const val = row[key2]
      return (
        val === null ||
        val === undefined ||
        val === '' ||
        typeof val === 'number' ||
        !Number.isNaN(parseFloat(String(val)))
      )
    })

    if (key1IsNumeric && !key2IsNumeric) {
      numericKey = key1
      labelKey = key2
    } else if (key2IsNumeric && !key1IsNumeric) {
      numericKey = key2
      labelKey = key1
    } else if (key1IsNumeric && key2IsNumeric) {
      // Both numeric - use second as value, first as label
      numericKey = key2
      labelKey = key1
    }

    if (numericKey && labelKey) {
      // Check if we have time-series data (dates/time)
      const timeKeywords = [
        'date',
        'time',
        'month',
        'year',
        'day',
        'week',
        'created',
        'updated',
      ]
      const hasTimeData = timeKeywords.some((keyword) =>
        labelKey?.toLowerCase().includes(keyword)
      )

      // Check if data looks like it should be a pie chart (categorical data with counts/percentages)
      const isPercentageData = data.some((row) => {
        const val = String(row[numericKey || ''] || '').toLowerCase()
        return val.includes('%') || val.includes('percent')
      })

      const isCategoricalCount =
        data.length <= 10 && // Not too many categories
        data.every((row) => row[numericKey || ''] >= 0) // All positive values

      if (isPercentageData || (isCategoricalCount && !hasTimeData)) {
        return 'pie'
      }

      return hasTimeData ? 'line' : 'bar'
    }
  }

  // If we have 3+ columns or mixed data types, default to table
  return 'table'
}

/**
 * Prepares data for chart visualization
 */
const prepareChartData = (data: DataPoint[], chartType: ChartType) => {
  if (!data || data.length === 0) return null

  const firstRow = data[0]
  const keys = Object.keys(firstRow)

  if (keys.length !== 2) return null

  // Determine which key is numeric and which is the label
  const [key1, key2] = keys
  let labelKey: string
  let valueKey: string

  // Check which column is primarily numeric
  const key1IsNumeric = data.every((row) => {
    const val = row[key1]
    return (
      val === null ||
      val === undefined ||
      val === '' ||
      typeof val === 'number' ||
      !Number.isNaN(parseFloat(String(val)))
    )
  })

  const key2IsNumeric = data.every((row) => {
    const val = row[key2]
    return (
      val === null ||
      val === undefined ||
      val === '' ||
      typeof val === 'number' ||
      !Number.isNaN(parseFloat(String(val)))
    )
  })

  if (key1IsNumeric && !key2IsNumeric) {
    valueKey = key1
    labelKey = key2
  } else if (key2IsNumeric && !key1IsNumeric) {
    valueKey = key2
    labelKey = key1
  } else if (key1IsNumeric && key2IsNumeric) {
    // Both numeric - use second as value, first as label (common SQL pattern)
    valueKey = key2
    labelKey = key1
  } else {
    // Neither clearly numeric, use original order
    labelKey = key1
    valueKey = key2
  }

  if (chartType === 'line' || chartType === 'bar') {
    const labels = data.map((row) => String(row[labelKey] || ''))
    const values = data.map((row) => {
      const val = row[valueKey]
      return typeof val === 'number' ? val : parseFloat(String(val)) || 0
    })

    return { labels, values, labelKey, valueKey }
  }

  if (chartType === 'pie') {
    return data.map((row, index) => ({
      id: index,
      label: String(row[labelKey] || ''),
      value:
        typeof row[valueKey] === 'number'
          ? row[valueKey]
          : parseFloat(String(row[valueKey])) || 0,
    }))
  }

  return null
}

export const DataVisualization = () => {
  const [query, setQuery] = useState('')
  const [showSql, setShowSql] = useState(false)
  const [results, setResults] = useState<{
    sql: string
    data: DataPoint[]
    error?: string
    metadata?: {
      executionTimeMs: number
      rowCount: number
      cacheHit: boolean
      queryIntent: string
      suggestedVisualization: string
      dataQualityScore: number
      businessWarnings?: string[]
      csInsights?: string[]
    }
  } | null>(null)

  const [generateVisualization, { loading }] = useMutation(
    GENERATE_DATA_VISUALIZATION,
    {
      onCompleted: (data) => {
        setResults(data.generateDataVisualization)
      },
      onError: (error) => {
        setResults({
          sql: '',
          data: [],
          error: error.message,
        })
      },
    }
  )

  const handleSubmit = async () => {
    if (!query.trim()) return

    try {
      await generateVisualization({
        variables: { naturalLanguageQuery: query },
      })
    } catch (error) {
      // Error handled by onError callback
    }
  }

  const handleExportCsv = () => {
    if (!results?.data || results.data.length === 0) return

    // Create CSV content manually
    const headers = Object.keys(results.data[0])
    const csvRows = [
      headers.join(','), // Header row
      ...results.data.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || ''
            // Escape values that contain commas or quotes
            const stringValue = String(value)
            if (
              stringValue.includes(',') ||
              stringValue.includes('"') ||
              stringValue.includes('\n')
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`
            }
            return stringValue
          })
          .join(',')
      ),
    ]

    const csvContent = csvRows.join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'data-visualization-results.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleExportChart = async () => {
    const chartElement = document.getElementById('chart-container')
    if (!chartElement) return

    try {
      const dataUrl = await htmlToImage.toPng(chartElement)
      const a = document.createElement('a')
      a.href = dataUrl
      a.download = 'chart.png'
      a.click()
    } catch (error) {
      // Error exporting chart - could show user notification
    }
  }

  const chartType = results?.data ? determineChartType(results.data) : 'table'
  const chartData = results?.data
    ? prepareChartData(results.data, chartType)
    : null

  const renderChart = () => {
    if (!chartData || !results?.data || results.data.length === 0) return null

    if (chartType === 'line' && 'labels' in chartData) {
      return (
        <LineChart
          width={800}
          height={400}
          series={[
            {
              data: chartData.values,
              label: chartData.valueKey,
            },
          ]}
          xAxis={[
            {
              data: chartData.labels,
              scaleType: 'point',
            },
          ]}
        />
      )
    }

    if (chartType === 'bar' && 'labels' in chartData) {
      return (
        <BarChart
          width={800}
          height={400}
          series={[
            {
              data: chartData.values,
              label: chartData.valueKey,
            },
          ]}
          xAxis={[
            {
              data: chartData.labels,
              scaleType: 'band',
            },
          ]}
        />
      )
    }

    if (chartType === 'pie' && Array.isArray(chartData)) {
      return (
        <PieChart
          series={[
            {
              data: chartData,
            },
          ]}
          width={800}
          height={400}
        />
      )
    }

    return null
  }

  const renderTable = () => {
    if (!results?.data || results.data.length === 0) return null

    const columns = Object.keys(results.data[0])

    return (
      <TableWrapper>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column}>
                    <Typography variant='subtitle2' fontWeight='bold'>
                      {column}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.data.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column}>
                      {String(row[column] || '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableWrapper>
    )
  }

  // Render CS insights and business warnings
  const renderCSInsights = () => {
    if (!results?.metadata) return null

    const {
      businessWarnings = [],
      csInsights = [],
      dataQualityScore = 0,
      queryIntent,
      cacheHit,
    } = results.metadata

    return (
      <Box sx={{ mb: 3 }}>
        {/* Data Quality & Performance */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(() => {
              if (dataQualityScore > 0.8) return <CheckCircle color='success' />
              if (dataQualityScore > 0.6) return <Warning color='warning' />
              return <Error color='error' />
            })()}
            <Typography variant='body2'>
              Data Quality: {(dataQualityScore * 100).toFixed(0)}%
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info color='info' />
            <Typography variant='body2'>
              Query Type: {queryIntent} {cacheHit && '(Cached)'}
            </Typography>
          </Box>
        </Box>

        {/* Business Warnings */}
        {businessWarnings.length > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Business Logic Warnings:
            </Typography>
            {businessWarnings.map((warning, index) => (
              <Typography
                key={`warning-${index}-${warning.substring(0, 10)}`}
                variant='body2'
              >
                • {warning}
              </Typography>
            ))}
          </Alert>
        )}

        {/* CS Insights */}
        {csInsights.length > 0 && (
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Insights:
            </Typography>
            {csInsights.map((insight, index) => {
              const [type, message] = insight.split(': ', 2)
              const severity = (() => {
                const lowerType = type.toLowerCase()
                if (lowerType === 'success') return 'success'
                if (lowerType === 'warning') return 'warning'
                if (lowerType === 'risk') return 'error'
                return 'info'
              })()

              return (
                <Alert
                  key={`insight-${index}-${type}-${message.substring(0, 10)}`}
                  severity={severity}
                  sx={{ mb: 1 }}
                >
                  {message}
                </Alert>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Container>
      <Header>
        <Typography variant='h4' gutterBottom>
          Data Chat
        </Typography>
        <Typography variant='body1' color='textSecondary'>
          Ask me anything about your organizations, cohorts, engagement, or
          coaching data. I'll understand your question and show you the
          insights.
        </Typography>
      </Header>

      <QuerySection>
        <QueryInput
          multiline
          rows={3}
          fullWidth
          variant='outlined'
          label='What would you like to know?'
          placeholder='e.g., "How are my organizations doing?" or "Any red flags I should know about?" or "Show me organizations that need attention"'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          disabled={loading}
        />

        <Box display='flex' gap={2} mt={2}>
          <ActionButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={!query.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Thinking...' : 'Ask'}
          </ActionButton>

          {results?.data && results.data.length > 0 && (
            <>
              <Button
                variant='outlined'
                startIcon={<FileDownload />}
                onClick={handleExportCsv}
              >
                Export CSV
              </Button>
              {chartType !== 'table' && (
                <Button
                  variant='outlined'
                  startIcon={<ImageIcon />}
                  onClick={handleExportChart}
                >
                  Export Chart
                </Button>
              )}
            </>
          )}
        </Box>
      </QuerySection>

      {results && (
        <ResultsSection>
          {results.error ? (
            <Alert severity='error' sx={{ mb: 2 }}>
              {results.error}
            </Alert>
          ) : (
            <>
              {/* CS Insights Section */}
              {renderCSInsights()}
              <Box display='flex' alignItems='center' gap={1} mb={2}>
                <Typography variant='h6'>
                  Results ({results.data.length} rows)
                </Typography>
                <IconButton
                  size='small'
                  onClick={() => setShowSql(!showSql)}
                  aria-label='toggle sql visibility'
                >
                  {showSql ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={showSql}>
                <SqlSection>
                  <Typography variant='subtitle2' gutterBottom>
                    Generated SQL:
                  </Typography>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <Typography
                      variant='body2'
                      component='pre'
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {results.sql}
                    </Typography>
                  </Paper>
                  <Divider sx={{ my: 2 }} />
                </SqlSection>
              </Collapse>

              {chartType !== 'table' && (
                <ChartContainer id='chart-container'>
                  <Typography variant='h6' gutterBottom>
                    Chart Visualization
                  </Typography>
                  {renderChart()}
                </ChartContainer>
              )}

              <Box mt={3}>
                <Typography variant='h6' gutterBottom>
                  Data Table
                </Typography>
                {renderTable()}
              </Box>
            </>
          )}
        </ResultsSection>
      )}
    </Container>
  )
}
